/**
 * Adyen's hosted checkout, used when the page the widget runs on is not an origin registered on the
 * Adyen client key (Drop-in refuses to mount there). The backend decides the mode per session; the
 * shopper then pays in a popup on Adyen's own page and the result comes back by postMessage from the
 * Calenso API's return page, or by polling that API - whichever arrives first.
 */
export const ADYEN_HOSTED = {
  /** `mode` values of the `POST adyen/sessions` answer. */
  MODE: {
    EMBEDDED: 'embedded',
    HOSTED: 'hosted',
  },
  /** Sent as `checkout_mode` by the iframe embed, which always pays on the hosted page. */
  CHECKOUT_MODE_HOSTED: 'hosted',
  /** Named window, so a second press reuses the popup instead of opening another one. */
  POPUP_NAME: 'calenso-adyen-pay',
  POPUP_FEATURES: 'popup,width=480,height=720',
  /** `source` tag the API's return page puts on its postMessage. */
  MESSAGE_SOURCE: 'calenso-adyen-hosted',
  /**
   * `source` tag of the reply `{source, nonce}` posted back to the return page once its message was
   * taken. Only that reply lets the page close itself; without it the page continues to `return_to`,
   * where a second widget would load inside the payment window.
   */
  ACK_SOURCE: 'calenso-adyen-hosted-ack',
  POLL_INTERVAL_MS: 2000,
  /**
   * Polling pace once the popup reads as closed. `closed` is not proof the shopper left: a
   * Cross-Origin-Opener-Policy on Adyen's or the issuer's page severs the opener, which then reports
   * `closed` for a window that is still open - and the return page's postMessage has no opener to
   * reach. Polling slowly until the session expires keeps such a payment from being lost.
   */
  CLOSED_POLL_INTERVAL_MS: 10_000,
  /**
   * How long polling stays at POLL_INTERVAL_MS while the window reads as open; after that it backs
   * off to CLOSED_POLL_INTERVAL_MS. The page coming back to the front always polls at once.
   */
  FAST_POLL_WINDOW_MS: 60_000,
  /** Polling limit when the session carries no `expires_at`: the backend mints hosted sessions for 25 minutes. */
  EXPIRY_FALLBACK_MS: 25 * 60 * 1000,
  /** `status` values of `GET adyen/hosted-result`. */
  RESULT_STATUS: {
    RETURNED: 'returned',
  },
  I18N: {
    PAY_BUTTON: 'adyen.hosted.pay_button',
    HINT: 'adyen.hosted.hint',
    POPUP_BLOCKED: 'adyen.hosted.popup_blocked',
    /** Iframe embed, popup blocked: the link below the message opens the payment instead. */
    ALLOW_POPUPS: 'adyen.hosted.allow_popups',
    COMPLETE_PAYMENT: 'adyen.hosted.complete_payment',
    RETURN_TO_TAB: 'adyen.hosted.return_to_tab',
    /** Shown instead of Cancel payment while the window was opened by the link: this page cannot close it. */
    CLOSE_WINDOW_FIRST: 'adyen.hosted.close_window_first',
    /** A payment tab that came back to the booking page with no record of that payment: send the shopper to the tab that has it. */
    PAYMENT_RECEIVED: 'adyen.hosted.payment_received',
  },
} as const;
